import type { DrugCard } from '@/entities/drug/api/drug.api';
import { DrugBadges } from '@/entities/drug/components/DrugBadges';
import { DrugMeta } from '@/entities/drug/components/DrugMeta';
import {
  drugKindLabels,
  displayDrugCode,
  formatFeeRate,
} from '@/entities/drug/utils/drug.utils';
import { DrugNameLink } from '@/shared/components/drug-drawer/DrugNameLink';
import { cn } from '@/shared/lib/utils';
import { formatWon, formatYm } from '@/shared/utils/format';
import { Fragment } from 'react';

/**
 * M-11 결과 카드 — Figma 검색_결과 (833:6047). 흰 카드 r20 · p16, 호버 시 1px 검정 테두리(833:6048).
 * 왼쪽: 품절 · 신규 필터링 금지 배지 / 약명 / 대조약 · 보험코드 · 제약사 · 급여 / 최소 처방 금액
 * 오른쪽: 약가 · 제약사 수수료율 · 사용자 수수료율 (각 120) + 의약품 정보 기준월
 * 카드 전체를 누르면 상세 드로어(M-12)를 연다. 수수료율이 없으면(거래 불가 · 미계약) "-%"를 연회색으로 낮추고 목록에서는 빼지 않는다.
 */
export const DrugResultCard = ({
  drug,
  active,
  onOpen,
}: {
  drug: DrugCard;
  /** 지금 드로어에 열려 있는 약 */
  active: boolean;
  onOpen: () => void;
}) => {
  const stats = [
    { label: '약가', value: formatWon(drug.price), rate: false, empty: false },
    {
      label: '제약사 수수료율',
      value: formatFeeRate(drug.pharmaFeeRate),
      rate: true,
      empty: drug.pharmaFeeRate == null,
    },
    {
      label: '사용자 수수료율',
      value: formatFeeRate(drug.userFeeRate),
      rate: true,
      empty: drug.userFeeRate == null,
    },
  ];

  return (
    <article
      onClick={onOpen}
      // 카드에 포커스가 잡혀야 드로어를 닫았을 때 이 카드로 포커스가 돌아온다 (M-15 D)
      tabIndex={0}
      aria-label={`${drug.drugName} 상세 보기`}
      onKeyDown={(e) => {
        if (
          e.target === e.currentTarget &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        // 테두리는 안쪽 1px(레이아웃이 흔들리지 않게 outline)
        'flex cursor-pointer flex-col gap-4 rounded-20 bg-surface-white p-4 outline-1 -outline-offset-1 outline-transparent transition-colors hover:outline-grey-900 focus-visible:ring-3 focus-visible:ring-brand-200 md:flex-row md:items-center',
        active && 'outline-grey-900',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
        <DrugBadges
          outOfStock={drug.outOfStock}
          newDrugBanned={drug.newDrugBanned}
        />
        <h3 className="text-body-15-sb text-fg-primary">
          <DrugNameLink drugCode={drug.drugCode} preview={drug}>
            {drug.drugName}
          </DrugNameLink>
        </h3>
        <DrugMeta
          className="text-body-13-r text-fg-tertiary"
          items={[
            ...drugKindLabels(drug),
            displayDrugCode(drug.drugCode),
            drug.pharmaCompanyName,
            drug.reimbursementType,
          ]}
        />
        {drug.minPrescriptionAmount != null && (
          <p className="text-body-13-r text-fg-tertiary">
            최소 처방 금액 {formatWon(drug.minPrescriptionAmount)}
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-3 rounded-8 bg-surface-grey pt-3 pb-2">
        <dl className="flex w-full items-stretch">
          {stats.map((s, i) => (
            <Fragment key={s.label}>
              {i > 0 && <span aria-hidden className="w-px bg-line-grey" />}
              <div className="flex flex-1 flex-col items-center gap-1 text-center md:w-[120px] md:flex-none">
                <dt className="text-body-13-r text-fg-tertiary">{s.label}</dt>
                <dd
                  className={cn(
                    'text-body-14-sb',
                    s.empty
                      ? 'text-fg-light'
                      : s.rate
                        ? 'text-fg-info'
                        : 'text-fg-primary',
                  )}
                >
                  {s.value}
                </dd>
              </div>
            </Fragment>
          ))}
        </dl>
        <p className="px-3 text-caption-11-r text-fg-tertiary">
          의약품 정보 기준 {formatYm(drug.referenceYearMonth)}
        </p>
      </div>
    </article>
  );
};
